// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { equals } from "ramda"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { grpc } from "../../../app/rpc/executor"
import { narratePreferencesClient } from "../../../app/rpc/client"
import { showSnackBar } from "../../snackbar/actions"
import { RootState } from "../../../app/store"
import {
  GetTrainingNarrativeRequest,
  TrainingNarrative,
} from "../../../generated/wisetime/user/assistant/v4/preferences_pb"

type GetTrainingNarrativeRpc = (request: GetTrainingNarrativeRequest) => Observable<TrainingNarrative>

export const getTrainingNarrative = (getTrainingNarrativeRpc: GetTrainingNarrativeRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.getTrainingNarrative.match),
      mergeMap(action => of(action.payload).pipe(
        mergeMap(getTrainingNarrativeRpc),
        withLatestFrom(state$),
        mergeMap(([trainingNarrative, state]) => {
          if (state.narratePreferences.previewNarratePreferences !== undefined
            && !equals(action.payload.toObject().preferences, state.narratePreferences.previewNarratePreferences)
          ) {
            return EMPTY
          }
          return of(Slice.setTrainingNarrative(trainingNarrative.toObject()))
        }),
        catchError(_ => of(showSnackBar({
          message: t({
            id: "msg_get_training_narrative_failed",
            message: "Failed to load narrative. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
          }),
          messageType: "ERROR",
        }))),
      )),
    )

const getTrainingNarrativeRpc = (request: GetTrainingNarrativeRequest): Observable<TrainingNarrative> =>
  grpc(() => narratePreferencesClient.getTrainingNarrative(request, null))

export default getTrainingNarrative(getTrainingNarrativeRpc)
